import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const SectionWrapper = styled.section`
  padding: 8rem 0;
  width: 100%;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 5rem 0;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 3rem;
  display: flex;
  flex-direction: ${({ $reversed }) => $reversed ? 'row-reverse' : 'row'};
  align-items: center;
  gap: 6rem;
  
  @media (max-width: 1024px) {
    gap: 4rem;
    padding: 0 2rem;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0 1.5rem;
    gap: 3rem;
  }
`;

const TextColumn = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const VisualColumn = styled(motion.div)`
  flex: 1.2;
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  min-height: 400px;
  
  @media (max-width: 768px) {
    width: 100%;
    min-height: 300px;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 2.5rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1.5rem;
  line-height: 1.2;
  letter-spacing: -0.01em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
`;

const SectionDescription = styled.p`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const CtaButton = styled.button`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 1rem;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  padding: 0;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: gap 0.2s ease;
  width: fit-content;
  border-bottom: 1px solid ${({ theme }) => theme.colors.text.primary};
  padding-bottom: 2px;
  
  &:hover {
    gap: 0.75rem;
    color: ${({ theme }) => theme.colors.primary.main};
    border-color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  min-height: 400px;
  background: ${({ theme }) => theme.colors.background.elevated};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`;

const ContentSection = ({ title, description, reversed, visual, ctaText = "Learn more" }) => {
    return (
        <SectionWrapper>
            <Container $reversed={reversed}>
                <TextColumn
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <SectionTitle>{title}</SectionTitle>
                    <SectionDescription>{description}</SectionDescription>
                    <CtaButton>
                        {ctaText} <span>→</span>
                    </CtaButton>
                </TextColumn>

                <VisualColumn
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    {visual || (
                        <ImagePlaceholder>
                            Visual Placeholder
                        </ImagePlaceholder>
                    )}
                </VisualColumn>
            </Container>
        </SectionWrapper>
    );
};

export default ContentSection;
